/**
 * Responsive board geometry.
 *
 * Tile size is derived from available space, not fixed — the same formula
 * runs on a wide laptop (height-constrained) and a narrow phone
 * (width-constrained): tileSize = floor(min(availW / cols, availH / rows)).
 *
 * The canvas element is always sized to exactly boardWidthPx x boardHeightPx
 * (never to the full available box), and centred within its container by
 * CSS flexbox. That means grid coordinates inside the canvas always start
 * at (0, 0) — no origin offset to carry through rendering and hit-testing.
 */

/** The comfortable touch target we get whenever the box is big enough for it. */
export const COMFORTABLE_TILE_SIZE = 44;

/**
 * The floor actually enforced when the box is NOT big enough.
 *
 * This used to be 44, enforced with Math.max, which quietly traded one problem
 * for a worse one. In a container too short for 44px tiles — the <iframe> on
 * dailyfbb.com is exactly that — the board grew taller than .board-wrap, and
 * since that wrap is overflow:auto with its contents centred, the top row was
 * pushed out of view entirely. A tile you cannot see is not easier to hit than
 * a small one.
 *
 * So the board now shrinks to fit, and this is the size below which it stops
 * being playable rather than merely tight. Hitting this floor means the
 * container is too small for the puzzle at all, and the fix is to give the
 * game more room — not to clip it.
 */
export const MIN_TILE_SIZE = 26;

export interface Layout {
  tileSize: number;
  boardWidthPx: number;
  boardHeightPx: number;
}

export function computeLayout(
  rows: number,
  cols: number,
  availWidth: number,
  availHeight: number,
): Layout {
  // Math.floor first: the largest whole-pixel tile that still fits the box.
  // Only then apply the floor, so the result is "as big as fits, but never
  // unplayably small" rather than "44 whether it fits or not".
  const fits = Math.floor(Math.min(availWidth / cols, availHeight / rows));
  const tileSize = Math.max(MIN_TILE_SIZE, fits);
  return {
    tileSize,
    boardWidthPx: tileSize * cols,
    boardHeightPx: tileSize * rows,
  };
}

export function tileCenter(layout: Layout, r: number, c: number): { x: number; y: number } {
  return {
    x: c * layout.tileSize + layout.tileSize / 2,
    y: r * layout.tileSize + layout.tileSize / 2,
  };
}

/** Inverse of tileCenter's grid: which cell (if any) contains point (x, y)? */
export function pointToCell(
  layout: Layout,
  rows: number,
  cols: number,
  x: number,
  y: number,
): { r: number; c: number } | null {
  const c = Math.floor(x / layout.tileSize);
  const r = Math.floor(y / layout.tileSize);
  if (r < 0 || r >= rows || c < 0 || c >= cols) return null;
  return { r, c };
}
